(() => {
  // Pack flavor picker on the product page (products tagged
  // 'choix-tablettes'). Mirrors the quick-shop picker (cg-quickshop.js):
  // the sum of per-flavor counters must reach data-required before any
  // order path proceeds, and each pick is written as a hidden
  // properties[Tablette N] input INSIDE the product form, so both Dawn's
  // AJAX add-to-cart (new FormData(form)) and the EasySell COD app embed
  // (which also reads properties[...] from the product form's FormData)
  // carry them onto the order.

  // EasySell only forwards line item properties to its COD orders when
  // this selector is configured (helpers.js getProperties() returns null
  // otherwise). Point it at Dawn's real product form — this also carries
  // the gift note. Only set on pages where this script loads (PDPs of
  // 'choix-tablettes' products).
  window.ES_PROPERTIES_FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';

  const packs = () => Array.from(document.querySelectorAll('[data-cg-pack]'));
  const propertyLabel = (pack) => pack.dataset.propertyLabel || 'Tablette';

  const formFor = (pack) =>
    document.getElementById(pack.dataset.formId) ||
    pack.closest('.shopify-section')?.querySelector('form[data-type="add-to-cart-form"]');

  // Dawn renders a second product form for payment terms (the installment
  // form, in the price block) which sits BEFORE the real product form in
  // the DOM. The EasySell COD app's getProductForm()/getProperties() pick
  // that first form, so the picks must be mirrored there too or COD orders
  // lose them.
  const allFormsFor = (pack) => {
    const forms = [formFor(pack)];
    const formId = pack.dataset.formId || '';
    const installment =
      document.getElementById(formId.replace('product-form-', 'product-form-installment-')) ||
      pack.closest('.shopify-section')?.querySelector('form[id^="product-form-installment-"]');
    forms.push(installment);
    return forms.filter(Boolean);
  };

  const totalPicks = (pack) =>
    Array.from(pack.querySelectorAll('[data-flavor-n]')).reduce(
      (sum, el) => sum + (parseInt(el.textContent, 10) || 0),
      0
    );

  const collectPicks = (pack) => {
    const picks = [];
    pack.querySelectorAll('.cg-pack__flavor').forEach((f) => {
      const n = parseInt(f.querySelector('[data-flavor-n]').textContent, 10) || 0;
      for (let i = 0; i < n; i++) picks.push(f.dataset.flavor);
    });
    return picks;
  };

  const isComplete = (pack) => totalPicks(pack) >= parseInt(pack.dataset.required, 10);

  const syncProps = (pack) => {
    const picks = collectPicks(pack);
    allFormsFor(pack).forEach((form) => {
      form.querySelectorAll('input[data-cg-pack-prop]').forEach((i) => i.remove());
      picks.forEach((title, i) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + propertyLabel(pack) + ' ' + (i + 1) + ']';
        input.value = title;
        input.setAttribute('data-cg-pack-prop', '');
        form.appendChild(input);
      });
    });
  };

  const updateCount = (pack) => {
    const required = parseInt(pack.dataset.required, 10);
    const total = totalPicks(pack);
    const counter = pack.querySelector('[data-pack-count]');
    if (counter) counter.textContent = total + '/' + required;
    pack.classList.toggle('is-full', total >= required);
    if (total >= required) {
      const hint = pack.querySelector('[data-pack-hint]');
      if (hint) hint.hidden = true;
    }
  };

  const flagMissing = (pack) => {
    pack.classList.add('is-missing');
    const hint = pack.querySelector('[data-pack-hint]');
    if (hint) hint.hidden = false;
    pack.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => pack.classList.remove('is-missing'), 1500);
  };

  document.addEventListener('click', (e) => {
    const flavor = e.target.closest('.cg-pack__flavor');
    if (!flavor) return;
    const pack = flavor.closest('[data-cg-pack]');
    if (!pack) return;
    const required = parseInt(pack.dataset.required, 10);
    const maxPerFlavor = parseInt(pack.dataset.maxPerFlavor, 10) || required;
    const nEl = flavor.querySelector('[data-flavor-n]');
    let n = parseInt(nEl.textContent, 10) || 0;
    if (e.target.closest('[data-flavor-minus]')) {
      n = Math.max(0, n - 1);
    } else if (totalPicks(pack) < required && n < maxPerFlavor) {
      n += 1;
    }
    nEl.textContent = n;
    flavor.querySelector('[data-flavor-count]').hidden = n === 0;
    flavor.classList.toggle('is-picked', n > 0);
    flavor.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
    updateCount(pack);
    syncProps(pack);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const el = document.activeElement;
    if (el && el.matches && el.matches('.cg-pack__flavor')) {
      e.preventDefault();
      el.click();
    }
  });

  // Gate the native product form submit ("Ajouter au panier"). A capture
  // listener on document runs before Dawn's ProductForm submit handler
  // (bound on the form itself), so stopPropagation() fully cancels the add
  // while the selection is incomplete.
  document.addEventListener(
    'submit',
    (e) => {
      const pack = packs().find((p) => formFor(p) === e.target);
      if (!pack) return;
      if (!isComplete(pack)) {
        e.preventDefault();
        e.stopPropagation();
        flagMissing(pack);
      } else {
        syncProps(pack);
      }
    },
    true
  );

  // Gate the EasySell COD button (app embed, e.g. button#es-sticky.es-button
  // appended to <body>). Clicks inside the EasySell modal are left alone:
  // the modal can only have opened after the selection was complete.
  document.addEventListener(
    'click',
    (e) => {
      if (!e.target.closest) return;
      if (e.target.closest('.es-modal, #easysell-modal, #easysell')) return;
      const esBtn = e.target.closest('.es-button, [id^="es-sticky"]');
      if (!esBtn) return;
      const incomplete = packs().find((p) => !isComplete(p));
      if (incomplete) {
        e.preventDefault();
        e.stopPropagation();
        flagMissing(incomplete);
      } else {
        packs().forEach(syncProps);
      }
    },
    true
  );
})();

